import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Loader2 } from "lucide-react";
import { Reveal, Overline, EASE } from "@/components/Reveal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  service_type: "",
  suburb: "",
  property_size: "",
  frequency: "",
  message: "",
};

const FAQS = [
  {
    q: "How quickly will I receive my quote?",
    a: "Within one business day. Most enquiries sent before 3pm receive a tailored quote the same afternoon.",
  },
  {
    q: "Do I need to provide cleaning products or equipment?",
    a: "No. Our specialists arrive with everything needed, including eco-conscious, family-safe products. If you prefer specific products, we're happy to use yours.",
  },
  {
    q: "Are your cleaners vetted and insured?",
    a: "Absolutely. Every TidyCare specialist is background-checked, fully insured and trained in our five-star housekeeping protocol before their first visit.",
  },
  {
    q: "What if I'm not happy with a clean?",
    a: "Our 24-hour guarantee means we return and re-perfect any area that falls short — completely free, no questions asked.",
  },
  {
    q: "Which Auckland suburbs do you cover?",
    a: "All central Auckland suburbs and the North Shore, including Ponsonby, Remuera, Parnell, Takapuna, the CBD and Epsom. Ask us about other areas.",
  },
];

const inputCls =
  "w-full rounded-xl border border-sand bg-white px-5 py-3.5 text-sm text-ink outline-none transition-colors duration-300 placeholder:text-stone/60 focus:border-gold";

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quote`, form);
      toast.success("Thank you — your quote request is in. We'll be in touch within one business day.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong sending your request. Please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.main
      data-testid="contact-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: EASE }}
      className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28"
    >
      <Reveal>
        <Overline>Get a Free Quote</Overline>
        <h1 className="mt-4 max-w-2xl font-serif text-4xl font-medium tracking-tight text-ink sm:text-5xl lg:text-6xl">
          Let's make your space <em className="text-gold-gradient not-italic">sparkle</em>
        </h1>
        <p className="mt-6 max-w-xl text-sm leading-relaxed text-stone sm:text-base">
          Tell us a little about your home or workplace and we'll send a
          tailored, no-obligation quote within one business day.
        </p>
      </Reveal>

      <div className="mt-14 grid gap-12 lg:grid-cols-12">
        <Reveal delay={0.1} className="lg:col-span-7">
          <form
            onSubmit={handleSubmit}
            data-testid="quote-form"
            className="rounded-3xl border border-sand bg-white p-8 lg:p-10"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-[0.16em] text-ink/70">Full Name *</label>
                <input data-testid="quote-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Smith" />
              </div>
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-[0.16em] text-ink/70">Email *</label>
                <input data-testid="quote-email-input" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="jane@email.co.nz" />
              </div>
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-[0.16em] text-ink/70">Phone *</label>
                <input data-testid="quote-phone-input" required value={form.phone} onChange={set("phone")} className={inputCls} placeholder="021 000 0000" />
              </div>
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-[0.16em] text-ink/70">Suburb *</label>
                <input data-testid="quote-suburb-input" required value={form.suburb} onChange={set("suburb")} className={inputCls} placeholder="Remuera" />
              </div>
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-[0.16em] text-ink/70">Service Type *</label>
                <select data-testid="quote-service-select" required value={form.service_type} onChange={set("service_type")} className={inputCls}>
                  <option value="" disabled>Select a service</option>
                  <option value="residential-regular">Residential — Regular Clean</option>
                  <option value="residential-deep">Residential — Deep Clean</option>
                  <option value="move-in-out">Move In / Move Out</option>
                  <option value="commercial-office">Commercial — Office</option>
                  <option value="commercial-retail">Commercial — Retail / Showroom</option>
                  <option value="commercial-deep">Commercial — Deep Reset</option>
                </select>
              </div>
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-[0.16em] text-ink/70">Property Size</label>
                <select data-testid="quote-size-select" value={form.property_size} onChange={set("property_size")} className={inputCls}>
                  <option value="">Select size</option>
                  <option value="1-2 bedrooms">1–2 bedrooms / small office</option>
                  <option value="3-4 bedrooms">3–4 bedrooms / medium office</option>
                  <option value="5+ bedrooms">5+ bedrooms / large premises</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-2 block text-xs font-bold uppercase tracking-[0.16em] text-ink/70">Preferred Frequency</label>
                <div className="flex flex-wrap gap-3" data-testid="quote-frequency-group">
                  {["One-off", "Weekly", "Fortnightly", "Monthly"].map((f) => (
                    <button
                      key={f}
                      type="button"
                      data-testid={`quote-freq-${f.toLowerCase()}`}
                      onClick={() => setForm({ ...form, frequency: f })}
                      className={`rounded-full border px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] transition-colors duration-300 ${
                        form.frequency === f
                          ? "btn-gold border-gold"
                          : "border-sand bg-white text-stone hover:border-gold/60 hover:text-gold-dark"
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-2 block text-xs font-bold uppercase tracking-[0.16em] text-ink/70">Anything else we should know?</label>
                <textarea
                  data-testid="quote-message-input"
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  className={`${inputCls} resize-none`}
                  placeholder="Pets, access notes, special requests..."
                />
              </div>
            </div>
            <button
              type="submit"
              data-testid="quote-submit-btn"
              disabled={sending}
              className="btn-gold mt-8 flex w-full items-center justify-center gap-3 rounded-full px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] disabled:opacity-70"
            >
              {sending && <Loader2 size={15} className="animate-spin" />}
              {sending ? "Sending..." : "Request My Free Quote"}
            </button>
          </form>
        </Reveal>

        <div className="lg:col-span-5">
          <Reveal delay={0.2}>
            <div data-testid="contact-info-card" className="rounded-3xl bg-obsidian p-8 lg:p-10">
              <h2 className="font-serif text-2xl font-medium text-ivory">Prefer to talk?</h2>
              <p className="mt-3 text-sm leading-relaxed text-ivory/60">
                Our Auckland team is happy to discuss your space and recommend
                the right level of care.
              </p>
              <ul className="mt-8 space-y-5 text-sm text-ivory/80">
                <li className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 text-gold"><Phone size={17} /></span>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-ivory/40">Phone</p>
                    <a data-testid="contact-phone-link" href="tel:+64225038702" className="transition-colors duration-300 hover:text-gold">022 503 8702</a>
                  </div>
                </li>
                <li className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 text-gold"><Mail size={17} /></span>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-ivory/40">Email</p>
                    <a data-testid="contact-email-link" href="mailto:tidycare@hotmail.com" className="transition-colors duration-300 hover:text-gold">tidycare@hotmail.com</a>
                  </div>
                </li>
                <li className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 text-gold"><MapPin size={17} /></span>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-ivory/40">Service Area</p>
                    <p>Auckland, New Zealand</p>
                  </div>
                </li>
                <li className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 text-gold"><Clock size={17} /></span>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-ivory/40">Hours</p>
                    <p>Mon – Sat, 7am – 7pm</p>
                  </div>
                </li>
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.3} className="mt-8">
            <div data-testid="faq-section" className="rounded-3xl border border-sand bg-white p-8 lg:p-10">
              <h2 className="font-serif text-2xl font-medium text-ink">Common questions</h2>
              <Accordion type="single" collapsible className="mt-4">
                {FAQS.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                    <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-sm font-semibold text-ink hover:text-gold-dark">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-stone">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </Reveal>
        </div>
      </div>
    </motion.main>
  );
}
